import type { ReceiptWidthMm } from './types';
import { receiptCharsPerLine, receiptPlainTextCols } from './format';

/** Centra una línea de `contentCols` en el ancho nativo del rollo (ESC/POS). */
export function receiptCenterLineOnPaper(
  line: string,
  contentCols: number,
  paperCols: number,
): string {
  const trimmed = line.slice(0, contentCols);
  if (paperCols <= trimmed.length) return trimmed;
  const pad = Math.floor((paperCols - trimmed.length) / 2);
  return `${' '.repeat(Math.max(0, pad))}${trimmed}`;
}

/** Recorta a `cols` sin padding (cuerpo del ticket en agente). */
export function receiptLeftLine(line: string, cols: number): string {
  return line.slice(0, cols);
}

export function receiptColsForBrowser(widthMm: ReceiptWidthMm): number {
  return receiptPlainTextCols(widthMm);
}

export function receiptColsForEscPos(widthMm: ReceiptWidthMm): number {
  return receiptCharsPerLine(widthMm);
}

/** Línea divisoria a ancho completo del ticket (browser o ESC/POS). */
export function receiptSeparatorLine(cols: number): string {
  const n = Math.max(8, Math.min(cols, 48));
  return '-'.repeat(n);
}

const AMOUNT_INDENT = '  ';
const CONTINUATION_INDENT = '  ';

/** Reserva columna de importe; una línea justificada (ESC/POS). Null si no entra. */
export function receiptJustifyLine(
  left: string,
  amount: string,
  cols: number,
): string | null {
  const price = amount.slice(0, cols);
  const maxLeft = cols - price.length - 1;
  if (maxLeft < 4 || left.length > maxLeft) return null;
  const pad = cols - left.length - price.length;
  if (pad < 1) return null;
  return `${left}${' '.repeat(pad)}${price}`;
}

export type ReceiptWrapOptions = { /** Menos “colgados” cortos al final de línea (ítems retail). */ aggressive?: boolean };

/** Parte por espacio cuando hay; palabra más larga que cols se corta dura. */
export function receiptWrapLine(line: string, cols: number, options?: ReceiptWrapOptions): string[] {
  if (cols < 8) return [line.slice(0, cols)];
  if (line.length <= cols) return [line];
  const orphanMax = options?.aggressive ? 8 : 14;
  const out: string[] = [];
  let rest = line;
  while (rest.length > cols) {
    const window = rest.slice(0, cols);
    let lastSpace = window.lastIndexOf(' ');
    let breakAt = lastSpace > 0 ? lastSpace : cols;
    const remainder = rest.slice(breakAt).trimStart();
    if (lastSpace > 0 && remainder.length > 0 && remainder.length < orphanMax) {
      const earlier = rest.slice(0, lastSpace).lastIndexOf(' ');
      if (earlier > Math.floor(cols * 0.35)) breakAt = earlier;
    }
    out.push(rest.slice(0, breakAt).trimEnd());
    rest = rest.slice(breakAt).trimStart();
  }
  if (rest) out.push(rest);
  return out;
}

function receiptNameLines(left: string, cols: number): string[] {
  return receiptWrapLine(left, cols, { aggressive: true }).map((l, i) =>
    i === 0
      ? l
      : `${CONTINUATION_INDENT}${l.slice(0, Math.max(1, cols - CONTINUATION_INDENT.length))}`,
  );
}

/** Importe en línea propia, alineado a la derecha del ancho del ticket (block retail). */
function receiptPriceLine(amount: string, cols: number): string {
  return receiptRightAlignLine(amount.slice(0, cols), cols);
}

/**
 * Ítem con precio — patrón retail estándar (POS térmico):
 * - **block** (browser): siempre línea(s) de nombre + línea de importe indentada.
 * - **escPos**: una línea justificada si entra; si no, mismo block que browser.
 */
export function receiptItemBlock(
  left: string,
  right: string,
  cols: number,
  options?: { escPos?: boolean },
): string[] {
  const amount = right.slice(0, cols);
  if (options?.escPos) {
    const one = receiptJustifyLine(left, amount, cols);
    if (one) return [one];
  }
  return [...receiptNameLines(left, cols), receiptPriceLine(amount, cols)];
}

/** @deprecated Prefer receiptPriceLine / block layout. */
export function receiptRightAlignLine(text: string, cols: number): string {
  const t = text.slice(0, cols);
  const pad = Math.max(0, cols - t.length);
  return `${' '.repeat(pad)}${t}`;
}

export function receiptTotalLine(label: string, amount: string, cols: number): string {
  const combined = `${label}: ${amount}`;
  if (combined.length <= cols) return combined;
  return `${label}:\n${receiptPriceLine(amount, cols)}`;
}

/** Texto de pie / legal: una o más líneas dentro de `cols`. */
export function receiptPlainTextLines(text: string, cols: number): string[] {
  const t = text.trim();
  if (!t) return [];
  return receiptWrapLine(t, cols);
}

/** Ítems sin precio (comanda). */
export function receiptKitchenItemBlock(
  qtyLine: string,
  optsLine: string | null,
  noteLine: string | null,
  cols: number,
): string[] {
  return [
    ...receiptNameLines(qtyLine, cols),
    ...(optsLine ? [`  ${optsLine.slice(0, cols - 2)}`] : []),
    ...(noteLine ? [`  Nota: ${noteLine.slice(0, cols - 8)}`] : []),
  ];
}

/** Opciones / nota debajo del ítem (precuenta, ticket). */
export function receiptItemExtrasBlock(
  optsLine: string | null,
  noteLine: string | null,
  cols: number,
): string[] {
  return [
    ...(optsLine ? [`  ${optsLine.slice(0, cols - 2)}`] : []),
    ...(noteLine ? [`  Nota: ${noteLine.slice(0, cols - 8)}`] : []),
  ];
}
